import { useState } from 'react';
import { 
  TrendingUp, 
  Search, 
  Shield, 
  Gauge, 
  Users, 
  Lock, 
  Smartphone, 
  BookOpen, 
  Globe, 
  AlertTriangle 
} from 'lucide-react';

interface UseCase {
  id: string;
  title: string;
  icon: any;
  description: string;
}

const useCases: UseCase[] = [
  {
    id: 'market-intelligence',
    title: 'Market & Price Intelligence',
    icon: TrendingUp,
    description: 'Collect product or pricing data from public e-commerce sites for competitive insight.'
  },
  {
    id: 'seo-monitoring',
    title: 'SEO & SERP Tracking',
    icon: Search,
    description: 'Monitor search rankings from different locations without bias or IP bans.'
  },
  {
    id: 'ad-verification',
    title: 'Ad Verification',
    icon: Shield,
    description: 'Check that ads display correctly and protect against ad fraud.'
  },
  {
    id: 'performance-testing',
    title: 'Web Performance Testing',
    icon: Gauge,
    description: 'Simulate users in multiple regions to analyze load times and CDN behavior.'
  },
  {
    id: 'social-media',
    title: 'Social Media Management',
    icon: Users,
    description: 'Manage multiple brand accounts across regions without account restrictions.'
  },
  {
    id: 'cybersecurity',
    title: 'Cybersecurity Research',
    icon: Lock,
    description: 'Gather threat intelligence without exposing the analyst\'s original IP.'
  },
  {
    id: 'qa-testing',
    title: 'App & Platform QA Testing',
    icon: Smartphone,
    description: 'Test features and behaviors in geo-specific or localized versions of apps.'
  },
  {
    id: 'academic-research',
    title: 'Critical Public Research',
    icon: BookOpen,
    description: 'Access public data sources for journalism, education, or research projects.'
  },
  {
    id: 'ux-testing',
    title: 'Localized UX Testing',
    icon: Globe,
    description: 'Preview how users in different countries or cities experience websites and apps.'
  },
  {
    id: 'brand-protection',
    title: 'Brand Protection',
    icon: AlertTriangle,
    description: 'Monitor unauthorized product listings or fake versions of your brand online.'
  }
];

const UseCasesSection = () => {
  const [expandedCase, setExpandedCase] = useState<string | null>(null);

  const handleBubbleClick = (id: string) => {
    setExpandedCase(expandedCase === id ? null : id);
  };

  return (
    <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-green-50/30 relative overflow-hidden section-pulse">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Common <span className="text-nexx-green relative">
              Use Cases
              <div className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-nexx-green to-green-400 rounded-full opacity-30"></div>
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            These are the most common and encouraged uses of our proxy network.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
          {useCases.map((useCase, index) => (
            <div
              key={useCase.id}
              className="group relative"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div
                onClick={() => handleBubbleClick(useCase.id)}
                className={`
                  relative cursor-pointer p-3 lg:p-4 rounded-xl border-2 transition-all duration-500 
                  transform hover:scale-105 hover:-translate-y-1
                  ${expandedCase === useCase.id 
                    ? 'bg-gradient-to-br from-nexx-green to-green-600 border-nexx-green shadow-xl shadow-nexx-green/25' 
                    : 'bg-white/80 backdrop-blur-sm border-gray-200/50 hover:border-nexx-green/50 hover:shadow-lg hover:shadow-nexx-green/10'
                  }
                `}
              >
                {/* Animated background glow */}
                <div className={`
                  absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500
                  bg-gradient-to-br from-nexx-green/5 to-green-400/5
                `}></div>

                <div className="relative z-10">
                  <div className="flex flex-col items-center text-center space-y-2">
                    <div className={`
                      w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center transition-all duration-500 transform group-hover:scale-110
                      ${expandedCase === useCase.id 
                        ? 'bg-white/20 text-white shadow-lg' 
                        : 'bg-gradient-to-br from-nexx-green/10 to-green-400/10 text-nexx-green group-hover:bg-gradient-to-br group-hover:from-nexx-green group-hover:to-green-600 group-hover:text-white'
                      }
                    `}>
                      <useCase.icon className="h-5 w-5 lg:h-6 lg:w-6" />
                    </div>
                    
                    <h3 className={`
                      font-bold text-xs lg:text-sm leading-tight transition-colors duration-500
                      ${expandedCase === useCase.id 
                        ? 'text-white' 
                        : 'text-gray-900 group-hover:text-nexx-green'
                      }
                    `}>
                      {useCase.title}
                    </h3>
                  </div>

                  {/* Expanded description */}
                  <div className={`
                    overflow-hidden transition-all duration-500 ease-out
                    ${expandedCase === useCase.id 
                      ? 'max-h-24 opacity-100 mt-3' 
                      : 'max-h-0 opacity-0'
                    }
                  `}>
                    <div className="pt-2 border-t border-white/20">
                      <p className="text-white/90 text-xs leading-relaxed">
                        {useCase.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Click indicator */}
                <div className={`
                  absolute -bottom-1 -right-1 w-5 h-5 bg-nexx-green rounded-full flex items-center justify-center
                  transition-all duration-300 transform
                  ${expandedCase === useCase.id 
                    ? 'scale-100 opacity-100' 
                    : 'scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-100'
                  }
                `}>
                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-16 left-8 w-16 h-16 bg-gradient-to-br from-nexx-green/10 to-green-400/10 rounded-full blur-xl animate-float-up" style={{ animationDuration: '8s', animationDelay: '1s' }}></div>
        <div className="absolute bottom-16 right-8 w-12 h-12 bg-gradient-to-br from-green-400/10 to-nexx-green/10 rounded-full blur-xl animate-float-up" style={{ animationDuration: '10s', animationDelay: '3s' }}></div>
      </div>
    </section>
  );
};

export default UseCasesSection;
